import { generateUniqueFieldName, nextClientId } from "@/lib/formBuilder/draft";
import type {
  FormBuilderDraft,
  FormBuilderField,
  FormBuilderSection,
} from "@/types/formBuilder";

export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function withFieldOrder(fields: FormBuilderField[]): FormBuilderField[] {
  return fields.map((field, index) => ({ ...field, order: index }));
}

export function withSectionOrder(
  sections: FormBuilderSection[],
): FormBuilderSection[] {
  return sections.map((section, index) => ({ ...section, order: index }));
}

export function moveSectionInDraft(
  draft: FormBuilderDraft,
  sectionClientId: string,
  direction: -1 | 1,
): FormBuilderDraft {
  const index = draft.sections.findIndex(
    (section) => section.clientId === sectionClientId,
  );
  if (
    index < 0 ||
    index + direction < 0 ||
    index + direction >= draft.sections.length
  ) {
    return draft;
  }
  return {
    ...draft,
    sections: withSectionOrder(
      reorder(draft.sections, index, index + direction),
    ),
  };
}

export function moveFieldInDraft(
  draft: FormBuilderDraft,
  fieldClientId: string,
  targetSectionClientId: string,
  targetIndex: number,
): FormBuilderDraft {
  const source = draft.sections.find((section) =>
    section.fields.some((field) => field.clientId === fieldClientId),
  );
  const target = draft.sections.find(
    (section) => section.clientId === targetSectionClientId,
  );
  if (!source || !target) return draft;

  const sourceIndex = source.fields.findIndex(
    (field) => field.clientId === fieldClientId,
  );
  const insertionIndex = Math.max(
    0,
    Math.min(targetIndex, target.fields.length - (source === target ? 1 : 0)),
  );
  if (source === target && sourceIndex === insertionIndex) return draft;

  const movedField = source.fields[sourceIndex];
  const sourceFields = source.fields.filter(
    (field) => field.clientId !== fieldClientId,
  );
  const targetFields = source === target ? sourceFields : [...target.fields];
  targetFields.splice(insertionIndex, 0, movedField);

  return {
    ...draft,
    sections: draft.sections.map((section) => {
      if (section === target) {
        return { ...section, fields: withFieldOrder(targetFields) };
      }
      if (section === source) {
        return { ...section, fields: withFieldOrder(sourceFields) };
      }
      return section;
    }),
  };
}

export function duplicateFieldInDraft(
  draft: FormBuilderDraft,
  sectionClientId: string,
  fieldClientId: string,
): FormBuilderDraft {
  const section = draft.sections.find(
    (item) => item.clientId === sectionClientId,
  );
  const fieldIndex =
    section?.fields.findIndex((field) => field.clientId === fieldClientId) ??
    -1;
  if (!section || fieldIndex < 0) return draft;

  const source = section.fields[fieldIndex];
  const usedNames = draft.sections.flatMap((item) =>
    item.fields.map((field) => field.name),
  );
  const copy: FormBuilderField = {
    ...source,
    clientId: nextClientId("field"),
    name: generateUniqueFieldName(usedNames, source.name || "field"),
    cityIds: [...source.cityIds],
    cityLabels: { ...source.cityLabels },
    choicesByMapText: { ...source.choicesByMapText },
  };
  delete copy.id;

  const fields = [...section.fields];
  fields.splice(fieldIndex + 1, 0, copy);
  return {
    ...draft,
    sections: draft.sections.map((item) =>
      item.clientId === sectionClientId
        ? { ...item, fields: withFieldOrder(fields) }
        : item,
    ),
  };
}

export function countSubmitHooks(raw: string): number {
  try {
    const parsed = JSON.parse(raw || "[]") as unknown;
    if (Array.isArray(parsed)) return parsed.length;
    if (parsed && typeof parsed === "object") return 1;
    return 0;
  } catch {
    return 0;
  }
}
